// Company Registrations — PUBLIC (unauthenticated) API client + TanStack Query
// hooks. Consumes backend app/api/v1/company_registrations public surface:
//   base = apiUrl('/company-registrations/public')
//
// Manual-fetch style (like document-studio.ts). No auth header — these routes
// are unauthenticated. The resume token is carried ONLY by the backend's
// Secure/HttpOnly/SameSite cookie: every call uses credentials:'include' so the
// browser attaches it automatically. JS never sees the token; there is no token
// in the path, body, or localStorage. The "current draft" is whatever the cookie
// resolves to (404 when there is none).
//
// Query keys: ['company-registration','public','current'].

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import { apiUrl } from '../lib/api'

const BASE = '/company-registrations/public'

// ─── Enums (mirror backend literals) ─────────────────────────────────────────
export type EntityType =
  | 'single_member_llc'
  | 'multi_member_llc'
  | 'c_corp'
  | 'other_unsure'
export type EffectiveDateType = 'asap' | 'future'
export type MailingAddressStatus = 'have' | 'need_assistance'
export type RegisteredAgentStatus = 'have' | 'need_assistance' | 'not_sure'
export type ManagementType = 'member_managed' | 'manager_managed' | 'not_sure'
export type TaxIdType = 'ssn' | 'itin' | 'foreign_none'
export type PaymentStatus =
  | 'not_started'
  | 'pending'
  | 'processing'
  | 'paid'
  | 'failed'
  | 'refunded'
  | 'disputed'
export type WorkflowStatus =
  | 'draft'
  | 'intake_received'
  | 'internal_review'
  | 'sent_to_accountant'
  | 'formation_filed'
  | 'formation_approved'
  | 'ein_submitted'
  | 'completed'
  | 'cancelled'

// ─── Config ──────────────────────────────────────────────────────────────────
export interface PublicConfig {
  enabled: boolean
  price_cents: number | null
  currency: string
  package_label: string
  included: string[]
}

// ─── Nested value shapes ─────────────────────────────────────────────────────
export interface OwnerInput {
  full_name: string
  address?: string | null
  ownership_percentage?: number | null
  sort_order?: number | null
}

export interface EinAnswers {
  reason_for_applying?: string | null
  date_business_started?: string | null
  closing_month?: string | null
  employees_agricultural?: number | null
  employees_household?: number | null
  employees_other?: number | null
  form_944?: boolean | null
  first_wages_date?: string | null
  principal_products?: string | null
  previous_ein_applied?: boolean | null
  previous_ein?: string | null
}

export type CompanyDetails = {
  mailing_address?: string | null
  physical_address?: string | null
  county?: string | null
} & Record<string, unknown>

export type ResponsiblePartyDetails = {
  address?: string | null
} & Record<string, unknown>

// ─── DraftUpdate (all optional) ──────────────────────────────────────────────
export interface DraftUpdate {
  locale?: string
  contact_full_name?: string | null
  contact_email?: string | null
  contact_phone?: string | null
  preferred_language?: string | null
  country_of_residence?: string | null

  requested_company_name_1?: string | null
  requested_company_name_2?: string | null
  requested_company_name_3?: string | null
  trade_name_dba?: string | null
  entity_type?: EntityType | null
  preferred_state?: string | null
  state_guidance_requested?: boolean | null
  principal_business_category?: string | null
  business_activity_description?: string | null
  desired_effective_date_type?: EffectiveDateType | null
  desired_effective_date?: string | null
  mailing_address_status?: MailingAddressStatus | null
  registered_agent_status?: RegisteredAgentStatus | null
  management_type?: ManagementType | null
  company_details?: CompanyDetails | null

  responsible_party_name?: string | null
  responsible_party_title?: string | null
  responsible_party_details?: ResponsiblePartyDetails | null
  tax_id_type?: TaxIdType | null
  tax_id_value?: string | null // WRITE-ONLY plaintext — only send, never read back
  owners?: OwnerInput[] | null
  number_of_members?: number | null

  ein_answers?: EinAnswers | null

  consent_version?: string | null
  certification_accepted?: boolean | null
  third_party_designee_authorized?: boolean | null
}

// ─── PublicRegistrationView (read) ───────────────────────────────────────────
export interface PublicRegistrationView {
  human_reference: string
  locale: string
  contact_full_name: string | null
  contact_email: string | null
  contact_phone: string | null
  preferred_language: string | null
  country_of_residence: string | null

  requested_company_name_1: string | null
  requested_company_name_2: string | null
  requested_company_name_3: string | null
  trade_name_dba: string | null
  entity_type: EntityType | null
  preferred_state: string | null
  state_guidance_requested: boolean | null
  principal_business_category: string | null
  business_activity_description: string | null
  desired_effective_date_type: EffectiveDateType | null
  desired_effective_date: string | null
  mailing_address_status: MailingAddressStatus | null
  registered_agent_status: RegisteredAgentStatus | null
  management_type: ManagementType | null
  company_details: CompanyDetails | null

  responsible_party_name: string | null
  responsible_party_title: string | null
  responsible_party_details: ResponsiblePartyDetails | null
  tax_id_type: TaxIdType | null
  tax_id_last4: string | null
  owners: OwnerInput[]
  number_of_members: number | null

  ein_answers: EinAnswers | null

  consent_version: string | null
  certification_accepted: boolean | null
  third_party_designee_authorized: boolean | null

  payment_status: PaymentStatus
  workflow_status: WorkflowStatus
  amount_cents: number | null
  currency: string | null
}

export interface CreateDraftBody {
  locale: string
  website?: string // HONEYPOT — always send empty
  source?: string
  referrer?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
}

export interface CreateDraftResponse {
  human_reference: string
  payment_status: PaymentStatus
  workflow_status: WorkflowStatus
}

export interface PreparePaymentResponse {
  human_reference: string
  client_secret: string
  publishable_key: string
  amount_cents: number
  currency: string
  package_label: string
  included: string[]
}

export interface PaymentStatusResponse {
  human_reference: string
  payment_status: PaymentStatus
  workflow_status: WorkflowStatus
  paid: boolean
}

// ─── fetch helper ────────────────────────────────────────────────────────────
export class PublicApiError extends Error {
  status: number
  code: string | null
  constructor(message: string, status: number, code: string | null) {
    super(message)
    this.status = status
    this.code = code
  }
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let detail: unknown
    try {
      detail = (await res.json()).detail
    } catch {
      detail = res.statusText
    }
    const obj =
      typeof detail === 'object' && detail ? (detail as Record<string, unknown>) : null
    const msg = obj && 'message' in obj
      ? String(obj.message)
      : typeof detail === 'string'
        ? detail
        : `Request failed (${res.status})`
    throw new PublicApiError(msg, res.status, obj && 'code' in obj ? String(obj.code) : null)
  }
  return (await res.json()) as T
}

// Same-origin credentialed fetch — the resume cookie rides along automatically.
const withCookie: RequestInit = { credentials: 'include' }

// ─── Config ──────────────────────────────────────────────────────────────────
export function usePublicConfig(): UseQueryResult<PublicConfig> {
  return useQuery<PublicConfig>({
    queryKey: ['company-registration', 'public', 'config'],
    queryFn: async () => jsonOrThrow(await fetch(apiUrl(`${BASE}/config`))),
    staleTime: 5 * 60 * 1000,
  })
}

const DRAFT_KEY = ['company-registration', 'public', 'current'] as const

// ─── Create draft ────────────────────────────────────────────────────────────
// Sets the resume cookie server-side; the token is never in the response.
export async function createDraft(
  body: CreateDraftBody,
): Promise<CreateDraftResponse> {
  return jsonOrThrow(
    await fetch(apiUrl(`${BASE}/drafts`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      ...withCookie,
      body: JSON.stringify(body),
    }),
  )
}

// ─── Fetch current draft (resume via cookie) ─────────────────────────────────
// 404 when the cookie resolves to no draft — retry:false so it stays a clean
// "no draft" signal rather than three retries.
export function useDraft(enabled: boolean): UseQueryResult<PublicRegistrationView> {
  return useQuery<PublicRegistrationView>({
    queryKey: DRAFT_KEY,
    enabled,
    retry: false,
    queryFn: async () =>
      jsonOrThrow(await fetch(apiUrl(`${BASE}/drafts/current`), withCookie)),
  })
}

// ─── Patch current draft ─────────────────────────────────────────────────────
export function usePatchDraft() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: DraftUpdate) =>
      jsonOrThrow<PublicRegistrationView>(
        await fetch(apiUrl(`${BASE}/drafts/current`), {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          ...withCookie,
          body: JSON.stringify(body),
        }),
      ),
    onSuccess: (data) => {
      qc.setQueryData(DRAFT_KEY, data)
    },
  })
}

// ─── Prepare payment (current draft) ─────────────────────────────────────────
export async function preparePayment(): Promise<PreparePaymentResponse> {
  return jsonOrThrow(
    await fetch(apiUrl(`${BASE}/drafts/current/prepare-payment`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      ...withCookie,
    }),
  )
}

// ─── Payment status (poll, current draft) ────────────────────────────────────
export async function fetchPaymentStatus(): Promise<PaymentStatusResponse> {
  return jsonOrThrow(
    await fetch(apiUrl(`${BASE}/drafts/current/payment-status`), withCookie),
  )
}
